/**
 * The directory's peek drawer: a 580px right-anchored panel that summarises a
 * brand without leaving the table, and steps through the *filtered* row set.
 */

import React from 'react';
import { Box, Drawer } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { brandDirectoryApi, BrandDirectoryRow } from '../../../services/brandDirectoryApi';
import { bvColor, bvRadius, bvShadow, bvType, formatCount, tabular } from '../brandVendorsTokens';
import { tokens } from '../../../theme/tokens';
import { lifecycleOf, specCoverageNote, specSchemaLine, tasksFor, brandMetaLine, STAGE_LABEL, regionsLabel, sellsLabel, websiteDomain, websiteHref } from '../brandLifecycle';
import { bvRoutes } from '../brandVendorsRoutes';
import { dealerVerifierSummary, useDealerVerifiers } from '../useDealerVerifiers';
import {
  BvButton,
  DefinitionRows,
  Eyebrow,
  IconArrowOut,
  IconClose,
  LogoFrame,
  StagePill,
  Stepper,
} from './BrandVendorsPrimitives';

interface LifecycleRow {
  id: string;
  label: string;
  state: string;
  tone: string;
  note: string;
  action?: { label: string; to: string };
}

const LIFECYCLE_ORDER: Array<{ id: string; label: string }> = [
  { id: 'added', label: 'Brand added' },
  { id: 'logo', label: 'Logo asset' },
  { id: 'ingestion', label: 'Ingestion method' },
  { id: 'products', label: 'Products in catalog' },
  { id: 'claimed', label: 'Claimed by a rep' },
];

const LifecycleAction: React.FC<{ action: { label: string; to: string } }> = ({ action }) => {
  const navigate = useNavigate();
  return (
    <BvButton size="sm" onClick={() => navigate(action.to)}>
      {action.label}
    </BvButton>
  );
};

export const BrandPeekDrawer: React.FC<{
  /** The filtered row set the stepper walks, in display order. */
  rows: BrandDirectoryRow[];
  brandId: string | null;
  onClose: () => void;
  onSelect: (brandId: string) => void;
}> = ({ rows, brandId, onClose, onSelect }) => {
  const navigate = useNavigate();
  const index = rows.findIndex((row) => row.id === brandId);
  const row = index >= 0 ? rows[index] : null;

  const { data: detail } = useQuery({
    queryKey: ['brand-directory-detail', brandId],
    queryFn: () => brandDirectoryApi.get(brandId as string),
    enabled: Boolean(brandId),
  });

  const { lookup: dealerLookup } = useDealerVerifiers();
  const dealer = dealerVerifierSummary(row && dealerLookup ? dealerLookup(row) : undefined);

  const facts = row ? lifecycleOf(row) : null;
  const tasks = row ? tasksFor(row, detail?.dataQualityPercent) : [];

  const lifecycleRows = React.useMemo<LifecycleRow[]>(() => {
    if (!row || !facts) return [];
    const byId = new Map((detail?.lifecycle || []).map((item) => [item.id, item]));
    return LIFECYCLE_ORDER.map((entry): LifecycleRow => {
      const item = byId.get(entry.id as never);
      switch (entry.id) {
        case 'added':
          return { ...entry, state: 'Done', tone: bvColor.success, note: 'Record exists in the registry.' };
        case 'logo':
          return {
            ...entry,
            state: facts.logo === 'svg' ? 'Complete' : facts.logo === 'raster' ? 'Raster only' : 'Missing',
            tone: facts.logo === 'svg' ? bvColor.success : facts.logo === 'raster' ? bvColor.warning : bvColor.danger,
            note:
              facts.logo === 'svg'
                ? 'Print-ready vector on file.'
                : facts.logo === 'raster'
                  ? 'Fine in tables, too small for print.'
                  : 'A monogram is used everywhere this brand appears.',
            action: { label: facts.logo === 'none' ? 'Upload' : 'Open library', to: bvRoutes.logos(row.id) },
          };
        case 'ingestion':
          return {
            ...entry,
            state: !facts.configured ? 'Missing' : facts.failing ? 'Failing' : 'Healthy',
            tone: !facts.configured ? bvColor.danger : facts.failing ? bvColor.warning : bvColor.success,
            note: facts.configured
              ? `${row.ingestion.source}${row.ingestion.paused ? ' · schedule off, runs only when started' : ''}`
              : 'Nothing is pulling this catalog.',
            action: { label: facts.configured ? 'Open brand' : 'Configure', to: bvRoutes.brand(row.id) },
          };
        case 'products':
          return {
            ...entry,
            state: facts.hasProducts ? 'Complete' : 'Missing',
            tone: facts.hasProducts ? bvColor.success : bvColor.danger,
            note: facts.hasProducts ? `${formatCount(row.productCount)} products in the catalog.` : 'Nothing ingested yet.',
          };
        default:
          return {
            ...entry,
            state: facts.claimed ? 'Claimed' : facts.claimStatus === 'requested' ? 'Requested' : 'Unclaimed',
            tone: facts.claimed ? bvColor.success : facts.claimStatus === 'requested' ? bvColor.warning : bvColor.danger,
            note: item?.note || (facts.claimed ? row.claimedBy.organizationName || '' : 'No rep from this brand has an account.'),
            action:
              facts.claimStatus === 'requested'
                ? { label: 'Review', to: bvRoutes.approvals(row.claimedBy.requestId) }
                : facts.claimStatus === 'unclaimed'
                  ? { label: 'Orgs', to: bvRoutes.organizations() }
                  : undefined,
          };
      }
    });
  }, [row, facts, detail]);

  return (
    <Drawer
      anchor="right"
      open={Boolean(row)}
      onClose={onClose}
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(17,24,39,0.28)' } } }}
      PaperProps={{
        sx: {
          width: { xs: '100%', md: 580 },
          bgcolor: bvColor.surface,
          borderLeft: `1px solid ${bvColor.border}`,
          boxShadow: bvShadow.drawer,
          backgroundImage: 'none',
        },
      }}
    >
      {row && facts && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* toolbar */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              px: '16px',
              height: 56,
              borderBottom: `1px solid ${bvColor.border}`,
              flexShrink: 0,
            }}
          >
            <Stepper
              vertical
              prevLabel="Previous brand"
              nextLabel="Next brand"
              prevDisabled={index <= 0}
              nextDisabled={index >= rows.length - 1}
              onPrev={() => index > 0 && onSelect(rows[index - 1].id)}
              onNext={() => index < rows.length - 1 && onSelect(rows[index + 1].id)}
            />
            <Box sx={{ ...bvType.caption, color: bvColor.muted, ...tabular }}>
              {index + 1} of {rows.length}
            </Box>
            <Box sx={{ flex: 1 }} />
            <BvButton size="sm" endIcon={<IconArrowOut size={12} />} onClick={() => navigate(bvRoutes.brand(row.id))}>
              Open brand page
            </BvButton>
            <BvButton size="sm" aria-label="Close" onClick={onClose} sx={{ width: 28, px: 0 }}>
              <IconClose size={13} />
            </BvButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', px: '16px', py: '16px' }}>
            {/* identity */}
            <Box sx={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
              <LogoFrame
                name={row.name}
                url={row.logoUrl}
                size={64}
                radius={bvRadius.logoFrameMedium}
                artSize={48}
                elevated
              />
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                  <Box sx={{ ...bvType.peekTitle, color: bvColor.ink }}>{row.name}</Box>
                  <StagePill stage={facts.stage} />
                </Box>
                <Box sx={{ ...bvType.caption, color: bvColor.muted, mt: '4px' }}>{brandMetaLine(row)}</Box>
              </Box>
            </Box>

            {/* lifecycle */}
            <Eyebrow sx={{ mt: '18px', mb: '4px' }}>Lifecycle</Eyebrow>
            <Box>
              {lifecycleRows.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    alignItems: 'center',
                    gap: '12px',
                    py: '13px',
                    borderBottom: `1px solid ${bvColor.borderSubtle}`,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                      <Box sx={{ ...bvType.tablePrimary, color: bvColor.ink }}>{item.label}</Box>
                      <Box sx={{ ...bvType.caption, color: item.tone, fontWeight: 500 }}>{item.state}</Box>
                    </Box>
                    {item.note && <Box sx={{ ...bvType.caption, color: bvColor.muted, mt: '3px' }}>{item.note}</Box>}
                  </Box>
                  {item.action ? <LifecycleAction action={item.action} /> : <span />}
                </Box>
              ))}
            </Box>

            {/* summary cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', mt: '16px' }}>
              {[
                {
                  eyebrow: 'Catalog',
                  value: row.productCount > 0 ? formatCount(row.productCount) : '—',
                  note: detail ? `${detail.dataQualityPercent}% field completeness` : 'products on file',
                },
                {
                  eyebrow: 'Retail reach',
                  value: detail ? `${detail.retailReach.carrying}` : '—',
                  note: detail ? `of ${detail.retailReach.totalStores} stores carry it` : 'stores carrying it',
                },
                {
                  eyebrow: 'Spec schema',
                  value:
                    detail?.specCoverage?.percent != null ? `${detail.specCoverage.percent}% mapped` : detail ? '—' : '…',
                  note: [specCoverageNote(row, detail?.specCoverage), specSchemaLine(detail?.specCoverage)]
                    .filter(Boolean)
                    .join(' · '),
                },
                {
                  eyebrow: 'Dealer check',
                  value: dealerLookup ? dealer.label : '—',
                  note: dealerLookup ? dealer.detail : 'Loading dealer lists…',
                },
              ].map((card) => (
                <Box
                  key={card.eyebrow}
                  sx={{ border: `1px solid ${bvColor.borderSubtle}`, borderRadius: `${bvRadius.panel}px`, p: '13px' }}
                >
                  <Eyebrow>{card.eyebrow}</Eyebrow>
                  <Box sx={{ fontSize: 14.5, fontWeight: 600, color: bvColor.ink, mt: '5px', ...tabular }}>
                    {card.value}
                  </Box>
                  <Box sx={{ ...bvType.caption, color: bvColor.muted, mt: '3px' }}>{card.note}</Box>
                </Box>
              ))}
            </Box>

            {/* contact */}
            <Eyebrow sx={{ mt: '18px', mb: '6px' }}>Contact</Eyebrow>
            <Box sx={{ border: `1px solid ${bvColor.borderSubtle}`, borderRadius: `${bvRadius.panel}px`, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', p: '13px' }}>
                <Box
                  aria-hidden
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: `${bvRadius.small}px`,
                    bgcolor: bvColor.ink,
                    color: bvColor.surface,
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  {(detail?.ownership.contactName || row.claimedBy.organizationName || '—').charAt(0).toUpperCase()}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ ...bvType.tablePrimary, color: bvColor.ink }}>
                    {detail?.ownership.contactName || 'No representative yet'}
                  </Box>
                  <Box sx={{ ...bvType.caption, color: bvColor.muted }}>
                    {row.claimedBy.organizationName || 'Unclaimed brand'}
                  </Box>
                </Box>
                <StagePill
                  stage={facts.claimed ? 'claimed' : facts.claimStatus === 'requested' ? 'requested' : 'registered'}
                  label={facts.claimed ? 'Claimed' : facts.claimStatus === 'requested' ? 'Requested' : 'Unclaimed'}
                />
              </Box>
              <DefinitionRows
                sx={{ borderTop: `1px solid ${bvColor.borderSubtle}` }}
                rows={[
                  { label: 'Email', value: detail?.ownership.contactEmail || '—' },
                  { label: 'Organization', value: row.claimedBy.organizationName || '—' },
                  {
                    label: 'Last sign-in',
                    value: detail?.ownership.lastSignIn
                      ? new Date(detail.ownership.lastSignIn).toLocaleDateString('en-US')
                      : '—',
                  },
                ]}
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  bgcolor: tokens.surface.subtle,
                  borderTop: `1px solid ${bvColor.borderSubtle}`,
                  px: '13px',
                  py: '11px',
                }}
              >
                <BvButton
                  size="sm"
                  onClick={() =>
                    navigate(
                      facts.claimStatus === 'requested'
                        ? bvRoutes.approvals(row.claimedBy.requestId)
                        : bvRoutes.organizations(row.claimedBy.organizationId || undefined)
                    )
                  }
                >
                  {facts.claimStatus === 'requested' ? 'Review claim' : 'Open organization'}
                </BvButton>
                <Box sx={{ ...bvType.caption, color: bvColor.muted }}>
                  {facts.claimStatus === 'requested'
                    ? 'Approving the claim verifies the org against this brand.'
                    : 'Ownership is managed from the organizations table.'}
                </Box>
              </Box>
            </Box>

            {/* record */}
            <Eyebrow sx={{ mt: '18px', mb: '6px' }}>Record</Eyebrow>
            <Box sx={{ border: `1px solid ${bvColor.borderSubtle}`, borderRadius: `${bvRadius.panel}px` }}>
              <DefinitionRows
                rows={[
                  {
                    label: 'Website',
                    value: websiteHref(row.website) ? (
                      <Box
                        component="a"
                        href={websiteHref(row.website) as string}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: bvColor.accent, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {websiteDomain(row.website)}
                      </Box>
                    ) : '—',
                  },
                  { label: 'Headquarters', value: row.country || '—' },
                  ...(row.parentCompany ? [{ label: 'Parent', value: row.parentCompany }] : []),
                  { label: 'Sells', value: sellsLabel(row.categories) || '—' },
                  { label: 'Regions', value: regionsLabel(row.regions) || '—' },
                  { label: 'Stage', value: STAGE_LABEL[facts.stage] },
                  { label: 'Outstanding', value: tasks.length ? `${tasks.length} task${tasks.length > 1 ? 's' : ''}` : 'Nothing outstanding' },
                ]}
              />
            </Box>
          </Box>

          {/* sticky footer */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              px: '16px',
              py: '11px',
              bgcolor: bvColor.page,
              borderTop: `1px solid ${bvColor.border}`,
              flexShrink: 0,
            }}
          >
            <BvButton size="sm" onClick={() => navigate(bvRoutes.directory)}>
              Merge into…
            </BvButton>
            <BvButton size="sm" tone="danger" onClick={() => navigate(bvRoutes.brand(row.id))}>
              Deactivate
            </BvButton>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ ...bvType.microCaption, color: bvColor.muted }}>
              {row.ingestion.lastRunAt
                ? `Updated ${new Date(row.ingestion.lastRunAt).toLocaleDateString('en-US')}`
                : 'Never synced'}
            </Box>
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default BrandPeekDrawer;
